// @ts-check
import { gql } from '@apollo/client';
import { Cloudinary } from '@cloudinary/url-gen';
import { pad } from '@cloudinary/url-gen/actions/resize';
import { color } from '@cloudinary/url-gen/qualifiers/background';
import { Image, View } from '@react-pdf/renderer';
import React from 'react';
import { Text, Title } from '../../../pdf-design-system';
import QRCode from '../components/LogoQRCode';
import * as exhibitor from '../exhibitor';
import * as rooms from '../helpers/rooms';
import theme from './vievinumStandDisplayTheme';

const cld = new Cloudinary({ cloud: { cloudName: 'bottlebooks' } });

// 150dpi
const mm = Math.round((1 / 25.4) * 150);

/** @param {{ data: import('./VieVinumStandDisplays').Table; bleed: number; }} props */
export default function VieVinumStandDisplay({ data, bleed }) {
  const table = afterQuery(data);
  return (
    <View style={{ flex: 1 }}>
      <StandDisplayHeader table={table} bleed={bleed} />

      <View style={{ marginTop: '45mm' }}>
        <Title variant={table.nameVariant} style={{ color: theme.colors.text }}>
          {table.nameLine1}
        </Title>
        <Title variant={table.nameVariant} style={{ color: theme.colors.text }}>
          {table.nameLine2}
        </Title>
      </View>

      <View style={{ marginTop: '11mm' }}>
        <Text variant="large" style={{ color: theme.colors.text }}>
          {table.region}
        </Text>
      </View>

      <Text style={{ marginTop: '19mm' }}>{table.associations}</Text>

      <StandDisplayFooter table={table} style={{ marginTop: 'auto' }} />
    </View>
  );
}

/** @param {{ table: ReturnType<afterQuery>; bleed: number; }} props */
function StandDisplayHeader({ table, bleed }) {
  const room = rooms.findRoomByStandName(table.stand?.name);
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      {table.zertifizierungslogos?.map((logoCode) => (
        <Image
          key={logoCode}
          src={logo(`vievinum/${logoCode}`, { width: 50, height: 50 })}
          style={{ width: '50mm', marginRight: '15mm' }}
        />
      ))}
      <View
        style={{
          marginLeft: 'auto',
          paddingLeft: '20mm',
          marginTop: `-${20 + bleed}mm`,
          paddingTop: `${20 + bleed}mm`,
          marginRight: `-${20 + bleed}mm`,
          paddingRight: `${20 + bleed}mm`,
          paddingBottom: '5mm',
          width: `${160 + 20 + bleed}mm`,
          backgroundColor: room.colors?.primary,
        }}
      >
        <Title
          variant="large"
          style={{
            paddingTop: '3mm',
            color: room.colors?.onPrimary || '#fff',
            textAlign: 'center',
          }}
        >
          {table.tableNumber}
        </Title>
      </View>
    </View>
  );
}

/** @param {{ table: ReturnType<afterQuery>; style: import('@react-pdf/types').Style }} props */
function StandDisplayFooter({ table, style }) {
  return (
    <View style={[{ flexDirection: 'row' }, style]}>
      {table.logo?.publicId && (
        <Image
          src={logo(table.logo.publicId, { width: 160, height: 160 })}
          style={{ width: '160mm' }}
        />
      )}
      <QRCode
        // Uppercasing the URL makes it simpler to encode, resulting in less complexity.
        url={`${table.siteUrl}/e/${table.companyId}`.toUpperCase()}
        logo="unserwein"
        margin={0}
        stroke={theme.colors.text}
        style={{ marginLeft: 'auto', width: '160mm', height: '160mm' }}
      />
    </View>
  );
}

/**
 * @param {string} publicId
 * @param {{ width: number; height: number; }} options
 */
function logo(publicId, { width, height }) {
  return cld
    .image(publicId)
    .format('png')
    .resize(
      pad()
        .background(color('#fff0'))
        .width(width * mm)
        .height(height * mm)
    )
    .toURL();
}

VieVinumStandDisplay.fragment = gql/* GraphQL */ `
  fragment VieVinumStandDisplay on SingleRegistration {
    companyId
    stand {
      name
    }
    profile {
      name
      region
      certifications
      associations
      logo {
        publicId
      }
    }
    collection {
      site {
        publicUrl
      }
      # IDs for apollo-client:
      ... on Event {
        eventId
      }
    }

    nameLine1: customFieldValue(key: "nameLine1") {
      __typename
      ... on TextFieldValue {
        value
      }
    }
    nameLine2: customFieldValue(key: "nameLine2") {
      __typename
      ... on TextFieldValue {
        value
      }
    }
    zertifizierungslogos: customFieldValue(key: "zertifizierungslogos") {
      __typename
      ... on MultiSelectFieldValue {
        values {
          key
        }
      }
    }
  }
`;

/**
 * @param {import('./@types/VieVinumStandDisplay').VieVinumStandDisplayFragment & Pick<import('./VieVinumStandDisplays').Table, 'tableNumber'>} data
 */
function afterQuery(data) {
  const siteUrl = data.collection?.site?.publicUrl?.replace(/\/$/, '');
  const line1 = text(data.nameLine1);
  const line2 = text(data.nameLine2);
  const name = data.profile?.name;
  const nameLine1 = exhibitor.nameLine1(name, line1, line2);
  const nameLine2 = exhibitor.nameLine2(name, line1, line2);
  const maxLength = Math.max(nameLine1.length, nameLine2.length);
  const nameVariant =
    maxLength > 23 ? 'smallest' : maxLength > 18 ? 'small' : 'default';
  return {
    ...data,
    ...data.profile,
    nameLine1,
    nameLine2,
    nameVariant,
    zertifizierungslogos: zertifizierungslogos(
      data.zertifizierungslogos,
      data.profile?.certifications
    ),
    siteUrl,
  };
}

/**
 * @param {import('./VieVinumStandDisplays').Table["nameLine1"]} customField
 * @returns {string}
 */
function text(customField) {
  if (customField?.__typename !== 'TextFieldValue') return '';
  return customField.value || '';
}

/**
 * @param {import('./VieVinumStandDisplays').Table["zertifizierungslogos"]} zertifizierungslogos
 * @param {string[] | null} certifications
 * @returns {('bio' | 'demeter' | 'sektAustria' | 'nachhaltigAustria')[] | undefined}
 */
function zertifizierungslogos(zertifizierungslogos, certifications) {
  if (zertifizierungslogos?.__typename === 'MultiSelectFieldValue') {
    if (zertifizierungslogos?.values?.length) {
      // @ts-ignore key is one of the four possible values above.
      return zertifizierungslogos?.values?.map(({ key }) => key);
    }
  }
  return certifications
    ?.map((certification) => certificationMapping[certification])
    .filter(Boolean);
}
const certificationMapping = {
  certifiedOrganicEU: 'bio',
  nachhaltig_austria: 'nachhaltigAustria',
  osterreichischer_sekt: 'sektAustria',
  demeter: 'demeter',
};
